import type { TraceFile } from '@terra/shared-schemas-ai';
import type { TraceToolCall } from '../types';
import { normalizeToolName } from './tool-config';
import type { getTraceFileType } from './toolsUtils';
import { getTraceFileLanguage } from './toolsUtils';

export const parseCodeResult = (content: string): string => {
  try {
    const parsed = JSON.parse(content);

    if (!parsed || typeof parsed !== 'object') {
      return content;
    }

    const parsedRecord = parsed as Record<string, unknown>;
    const outputValue = parsedRecord.output;
    const nestedOutput =
      typeof outputValue === 'object' && outputValue !== null
        ? (outputValue as Record<string, unknown>)
        : null;
    const result =
      parsedRecord.result !== undefined
        ? parsedRecord.result
        : nestedOutput?.result !== undefined
          ? nestedOutput.result
          : typeof outputValue === 'string'
            ? outputValue
            : undefined;

    if (result !== undefined) {
      if (typeof result === 'string' && result.trim()) {
        return result;
      }

      if (typeof result === 'object' && result !== null) {
        return JSON.stringify(result, null, 2);
      }
    }

    if (
      typeof parsedRecord.error === 'string' &&
      parsedRecord.error &&
      result === undefined
    ) {
      const errorDetails = [];
      errorDetails.push(`Error: ${parsedRecord.error}`);

      if (
        Array.isArray(parsedRecord.stderr) &&
        parsedRecord.stderr.length > 0
      ) {
        errorDetails.push('\nStderr:');
        errorDetails.push(
          ...parsedRecord.stderr
            .filter((entry): entry is string => typeof entry === 'string')
            .map((entry) => entry),
        );
      }

      return errorDetails.join('\n');
    }

    return content;
  } catch {
    return content;
  }
};

export const formatContentForDisplay = (
  content: string,
  fileType: ReturnType<typeof getTraceFileType>,
): string => {
  const language = getTraceFileLanguage(content, fileType);

  if (fileType === 'markdown') {
    return content;
  }

  if (content.includes('```')) {
    return content;
  }

  return `\`\`\`${language}\n${content}\n\`\`\``;
};

function extractUuid(toolCallId: string, output?: string): string | undefined {
  const uuidPattern =
    /[a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12}/gi;

  const idMatch = toolCallId.match(uuidPattern);
  if (idMatch) return idMatch[0];

  if (output) {
    const outputMatch = output.match(uuidPattern);
    if (outputMatch) return outputMatch[0];
  }

  return undefined;
}

export function findRelatedTraceFiles(
  toolCall: TraceToolCall,
  findTraceFiles?: (pattern: string | RegExp) => TraceFile[],
  getTraceFile?: (idOrName: string) => TraceFile | undefined,
  allTraceFiles?: TraceFile[],
  toolCallIndex?: number,
): { codeFile?: TraceFile; resultFile?: TraceFile } {
  if (!findTraceFiles && !getTraceFile) return {};

  const normalizedName = normalizeToolName(toolCall.name);
  const { fileMetadata } = toolCall;

  if (fileMetadata && getTraceFile) {
    const codeFile = fileMetadata.codeWithSecretsPath
      ? getTraceFile(fileMetadata.codeWithSecretsPath)
      : undefined;
    const resultFile = fileMetadata.codeResultPath
      ? getTraceFile(fileMetadata.codeResultPath)
      : fileMetadata.intelligenceResponsePath
        ? getTraceFile(fileMetadata.intelligenceResponsePath)
        : undefined;

    if (codeFile || resultFile) {
      return { codeFile, resultFile };
    }
  }

  if (!findTraceFiles) return {};

  const uuid = extractUuid(toolCall.id, toolCall.output);

  switch (normalizedName) {
    case 'coder':
    case 'python':
    case 'python-tool':
    case 'sub-agent-coder': {
      if (uuid) {
        const codeFiles = findTraceFiles(new RegExp(`code_${uuid}\\.py$`, 'i'));
        const resultFiles = findTraceFiles(
          new RegExp(`code_result_${uuid}`, 'i'),
        );
        if (codeFiles.length > 0 || resultFiles.length > 0) {
          return {
            codeFile: codeFiles[0],
            resultFile: resultFiles[0],
          };
        }
      }

      if (allTraceFiles) {
        const codeFiles = allTraceFiles
          .filter(
            (file) =>
              file.name.startsWith('code_') && file.name.endsWith('.py'),
          )
          .sort(
            (a, b) =>
              new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime(),
          );

        if (typeof toolCallIndex === 'number' && toolCallIndex >= 0) {
          const codeFile = codeFiles[toolCallIndex];
          if (codeFile) {
            const uuidMatch = codeFile.name.match(
              /code_([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i,
            );
            if (uuidMatch) {
              const fileUuid = uuidMatch[1];
              const resultFile = allTraceFiles.find(
                (file) =>
                  file.name === `code_result_${fileUuid}.txt` ||
                  file.name.startsWith(`code_result_${fileUuid}`),
              );
              return { codeFile, resultFile };
            }
            return { codeFile };
          }
        }

        if (codeFiles.length > 0) {
          const codeFile = codeFiles[0];
          const uuidMatch = codeFile.name.match(
            /code_([a-f0-9]{8}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{4}-[a-f0-9]{12})/i,
          );
          if (uuidMatch) {
            const fileUuid = uuidMatch[1];
            const resultFile = allTraceFiles.find(
              (file) =>
                file.name === `code_result_${fileUuid}.txt` ||
                file.name.startsWith(`code_result_${fileUuid}`),
            );
            return { codeFile, resultFile };
          }
        }
      }

      return {};
    }
    case 'sub-agent-intelligence': {
      if (!findTraceFiles) return {};

      const intelligenceFiles = findTraceFiles(
        new RegExp(`intelligence_response_.*\\.md$`, 'i'),
      );
      if (intelligenceFiles.length > 0) {
        return {
          codeFile: undefined,
          resultFile: intelligenceFiles[0],
        };
      }
      return {};
    }
    default:
      return {};
  }
}
